import { useEffect, useState } from 'react';
import {
  Box,
  IconButton,
  Typography,
  useTheme,
  useMediaQuery,
  Select,
  MenuItem,
  Skeleton,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import dayjs from 'dayjs';
import weekday from 'dayjs/plugin/weekday';
import { MONTHS, WEEKDAYS } from '../../../../../util/constants';

dayjs.extend(weekday);

const getMonthName = month => {
  return MONTHS[month - 1];
};

function calculateDateChange(month, year, change) {
  month += change;
  while (month > 12) {
    month -= 12;
    year++;
  }
  while (month <= 0) {
    month += 12;
    year--;
  }
  return { month, year };
}

const CalendarDay = ({ day, price, priceColor, state, onClick, style, isOriginal, testId }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isVerySmall = useMediaQuery(theme.breakpoints.down('xs'));

  const isDark = theme.palette.mode === 'dark';

  const getBackgroundColor = () => {
    if (state === 'disabled') {
      // Dark mode: ghost-like, blends with the paper so that bookable days clearly stand out.
      return isDark ? 'transparent' : theme.palette.action.disabledBackground;
    }
    if (state === 'active') {
      return theme.palette.primary.main;
    }
    return isDark ? theme.palette.grey[700] : theme.palette.grey[100];
  };

  const getTextColor = () => {
    if (state === 'disabled') {
      return isDark
        ? alpha(theme.palette.common.white, 0.3)
        : theme.palette.text.disabled;
    }
    if (state === 'active') return theme.palette.primary.contrastText;
    return theme.palette.text.primary;
  };

  const getBorderColor = () => {
    if (isOriginal) return theme.palette.primary.main;
    if (state === 'disabled') {
      return isDark ? alpha(theme.palette.common.white, 0.06) : theme.palette.divider;
    }
    if (state === 'active') return theme.palette.primary.dark;
    return theme.palette.divider;
  };

  const getPriceTextColor = () => {
    if (state === 'active') return alpha(theme.palette.primary.contrastText, 0.85);
    if (priceColor === 'green') return theme.palette.success.main;
    if (priceColor === 'red') return theme.palette.error.main;
    return theme.palette.text.secondary;
  };

  const getHoverStyle = () => {
    if (state === 'normal') {
      return {
        backgroundColor: isDark ? theme.palette.grey[600] : theme.palette.action.hover,
        '& .price-text': {
          color: theme.palette.text.primary,
        },
      };
    }
    if (state === 'active') {
      return { backgroundColor: theme.palette.primary.dark };
    }
    return {};
  };

  const dayFontSize = isVerySmall ? '0.75rem' : isMobile ? '0.875rem' : '1.25rem';
  const priceFontSize = isVerySmall ? '0.5rem' : isMobile ? '0.625rem' : '0.75rem';
  const dayHeight = isVerySmall ? '32px' : isMobile ? '40px' : '55px';
  const showPrice = !isMobile;

  return (
    <Box
      onClick={onClick}
      data-testid={testId}
      sx={{
        ...style,
        height: dayHeight,
        minHeight: dayHeight,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: getBackgroundColor(),
        color: getTextColor(),
        cursor: state === 'disabled' ? 'default' : 'pointer',
        userSelect: 'none',
        border: `1px solid ${getBorderColor()}`,
        borderWidth: isOriginal ? '2px' : '1px',
        borderRadius: '10px',
        position: 'relative',
        transition: 'background-color 120ms ease-out, color 120ms ease-out',
        '&:hover': getHoverStyle(),
      }}
    >
      <Typography
        variant='body1'
        sx={{
          fontSize: dayFontSize,
          fontWeight: 500,
          lineHeight: 1,
          mb: price && showPrice ? 0.25 : 0,
        }}
      >
        {day}
      </Typography>
      {price && showPrice && (
        <Typography
          className='price-text'
          variant='caption'
          sx={{
            fontSize: priceFontSize,
            color: getPriceTextColor(),
            lineHeight: 1,
          }}
        >
          {price}
        </Typography>
      )}
    </Box>
  );
};

export default function DatePicker(props) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [selectedIndex, setSelectedIndex] = useState(null);
  const isLoading = !props.data;

  useEffect(() => {
    if (!props.selectedDate || !props.data) {
      setSelectedIndex(null);
      return;
    }

    const baseDate = dayjs(new Date(props.year, props.month - 1, 1));
    const daysToSubtract = baseDate.day() === 0 ? 6 : baseDate.day() - 1;
    const firstDay = baseDate.subtract(daysToSubtract, 'day');
    const selectedDateObj = dayjs(props.selectedDate);
    const daysDiff = selectedDateObj.diff(firstDay, 'day');

    setSelectedIndex(daysDiff);
  }, [props.selectedDate, props.data, props.month, props.year]);

  useEffect(() => {
    setSelectedIndex(null);
  }, [props.month, props.year]);

  useEffect(() => {
    if (selectedIndex !== null) {
      props.onChange?.(firstDay.add(selectedIndex, 'day').format('YYYY-MM-DD'));
    } else {
      props.onChange?.(null);
    }
  }, [selectedIndex]);

  const baseDate = dayjs(new Date(props.year, props.month - 1, 1));
  // Calculate the first day of the month
  const daysToSubtract = baseDate.day() === 0 ? 6 : baseDate.day() - 1;
  const firstDay = baseDate.subtract(daysToSubtract, 'day');

  const lastDayOfMonth = dayjs(new Date(props.year, props.month, 0));
  const firstDayOfMonth = dayjs(new Date(props.year, props.month - 1, 1));
  const daysInMonth = lastDayOfMonth.date();
  const firstDayWeekday = firstDayOfMonth.day() === 0 ? 6 : firstDayOfMonth.day() - 1;
  const weeks = Math.ceil((firstDayWeekday + daysInMonth) / 7);

  const dayDataMap = props.data ? new Map(props.data.map(day => [day.day, day])) : new Map();
  const totalDays = weeks * 7;

  // Normalise a day price to a finite number. The availability endpoints format
  // some of their amounts server-side, so a price can arrive as a string.
  const toFiniteNumber = value => {
    if (value === null || value === undefined || value === '') return null;
    const numeric = typeof value === 'number' ? value : parseFloat(String(value).replace(',', '.'));
    return Number.isFinite(numeric) ? numeric : null;
  };

  // Calculate price ranges for color coding
  const prices = Array.from(dayDataMap.values())
    .map(day => toFiniteNumber(day.minPrice))
    .filter(price => price !== null)
    .sort((a, b) => a - b);

  const priceRanges = {
    low: prices[Math.floor(prices.length / 3)],
    high: prices[Math.ceil((prices.length * 2) / 3)],
  };

  const getPriceColor = price => {
    const numeric = toFiniteNumber(price);
    if (!numeric) return 'black';
    if (numeric <= priceRanges.low) return 'green';
    if (numeric >= priceRanges.high) return 'red';
    return 'black';
  };

  const formatPrice = price => {
    const numeric = toFiniteNumber(price);
    if (!numeric) return '';
    return numeric.toFixed(2).replace('.', ',') + '€';
  };

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100%',
        animation: isLoading ? 'none' : 'fadeInUp 0.6s ease-out',
        '@keyframes fadeInUp': {
          '0%': {
            opacity: 0,
            transform: 'translateY(10px)',
          },
          '100%': {
            opacity: 1,
            transform: 'translateY(0)',
          },
        },
      }}
    >
      {/* Navigation Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          px: 1,
        }}
      >
        <IconButton
          onClick={() =>
            !props.disabled && props.onNavigate?.(calculateDateChange(props.month, props.year, -1))
          }
          disabled={props.disabled}
          size={isMobile ? 'small' : 'medium'}
          sx={{ color: theme.palette.text.secondary }}
        >
          <ArrowBackIcon />
        </IconButton>

        {/* Month-Year dropdown selection */}
        <Select
          variant='outlined'
          size={isMobile ? 'small' : 'medium'}
          value={`${props.year}-${props.month}`}
          onChange={e => {
            const [year, month] = e.target.value.split('-').map(Number);
            props.onNavigate?.({ month, year });
          }}
          disabled={props.disabled}
          sx={{
            '.MuiSelect-select': {
              p: 0,
              display: 'flex',
              alignItems: 'center',
              fontSize: isMobile ? '1rem' : '1.25rem',
              fontWeight: 500,
            },
            minWidth: isMobile ? 120 : 160,
            '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
          }}
        >
          {Array.from({ length: 24 }).map((_, idx) => {
            const date = dayjs().startOf('month').add(idx, 'month');
            const month = date.month() + 1;
            const year = date.year();
            return (
              <MenuItem key={`${year}-${month}`} value={`${year}-${month}`}>
                {getMonthName(month)} {year}
              </MenuItem>
            );
          })}
        </Select>

        <IconButton
          onClick={() =>
            !props.disabled && props.onNavigate?.(calculateDateChange(props.month, props.year, 1))
          }
          disabled={props.disabled}
          size={isMobile ? 'small' : 'medium'}
          sx={{ color: theme.palette.text.secondary }}
        >
          <ArrowForwardIcon />
        </IconButton>
      </Box>

      {/* Weekday Headers */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: 1,
          mb: 1,
        }}
      >
        {WEEKDAYS.map(weekday => (
          <Typography
            key={weekday}
            variant='body2'
            sx={{
              textAlign: 'center',
              fontWeight: 600,
              color: theme.palette.text.secondary,
              py: 1,
            }}
          >
            {weekday}
          </Typography>
        ))}
      </Box>

      {/* Calendar Grid */}
      {isLoading || props.disabled ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gridTemplateRows: `repeat(${weeks}, 1fr)`,
            gap: 1,
            width: '100%',
          }}
        >
          {Array.from({ length: totalDays }).map((_, index) => (
            <Skeleton key={index} variant='rectangular' sx={{ borderRadius: '10px' }} height={55} />
          ))}
        </Box>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gridTemplateRows: `repeat(${weeks}, 1fr)`,
            gap: 1,
            width: '100%',
            borderRadius: 1,
            overflow: 'hidden',
          }}
        >
          {Array.from({ length: totalDays }).map((_, index) => {
            const currentDate = firstDay.add(index, 'day');
            const dayData = dayDataMap.get(currentDate.date());
            const weekIndex = Math.floor(index / 7);
            const dayIndex = index % 7;
            const isCurrentMonth = currentDate.month() + 1 === props.month;
            const isBookable = !!dayData?.isBookable;
            const testId =
              isCurrentMonth && isBookable
                ? `create-booking-day-${currentDate.format('YYYY-MM-DD')}`
                : undefined;

            return (
              <CalendarDay
                key={index}
                day={currentDate.date()}
                isOriginal={dayData?.isOriginal}
                testId={testId}
                price={
                  currentDate.month() + 1 !== props.month
                    ? ''
                    : dayData?.minPrice
                      ? formatPrice(dayData.minPrice)
                      : ''
                }
                priceColor={
                  currentDate.month() + 1 !== props.month
                    ? 'default'
                    : getPriceColor(dayData?.minPrice)
                }
                state={
                  currentDate.month() + 1 !== props.month
                    ? 'disabled'
                    : !dayData?.isBookable
                      ? 'disabled'
                      : index === selectedIndex
                        ? 'active'
                        : 'normal'
                }
                style={{
                  gridColumn: dayIndex + 1,
                  gridRow: weekIndex + 1,
                }}
                onClick={() => {
                  if (!dayData?.isBookable || selectedIndex === index) return;
                  else setSelectedIndex(index);
                }}
              />
            );
          })}
        </Box>
      )}
    </Box>
  );
}
